import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import CallExecutivePipelineBar from "components/Common/CallExecutivePipelineBar";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import { submitCallExecutiveTodayFilterData, submitCallExecutiveQuickUpdate } from "model/CallModel";
import { useCallExecutiveContext } from "libs/context/CallExecutiveContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import VideoInterviewLinkModal from "pages/AllCommonModal/VideoInterviewLinkModal/video-interview-link-modal";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import {
  DEFAULT_PAGE_SIZE,
  appendCallExecutiveQuickUpdateColumnIfMissing,
  buildCallExecutiveTableColumnsFromMetadata,
  mapApiMetadataTableColumns,
  resolveCallExecutiveQuickUpdateCandidateClientId,
  resolveCallExecutiveQuickUpdateRowValue,
  urlEndPoint,
} from "helpers/GeneralHelper";
import {
  ASYNC_AUTOCOMPLETE_API_TO_KIND,
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCallExecutiveAppliedFilter } from "hooks/useCallExecutiveAppliedFilter";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import {
  resolveServerPageSizeMeta,
} from "components/Common/ServerListPagination";

const CALL_EXECUTIVE_TODAY_PREFILL_STORAGE_KEY = "callExecutiveTodayPrefillFilters";

const readStoredPrefillFilters = () => {
  try {
    const raw = sessionStorage.getItem(CALL_EXECUTIVE_TODAY_PREFILL_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CALL_EXECUTIVE_TODAY_PREFILL_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const isClientFilterDefinition = (definitionRow = {}) => {
  const asyncKind =
    definitionRow?.asyncKind ||
    ASYNC_AUTOCOMPLETE_API_TO_KIND[definitionRow?.api];
  if (asyncKind === "client") return true;
  return String(definitionRow?.field ?? "").trim().toLowerCase() === "clientid";
};

const resolveClientFilterDefinition = (metadataFilters = []) =>
  (Array.isArray(metadataFilters) ? metadataFilters : []).find(isClientFilterDefinition);

const resolveFilterDefinitionByFieldOrApi = (
  metadataFilters = [],
  fieldNames = [],
  apiKeys = []
) => {
  const normalizedFields = fieldNames.map((fieldName) =>
    String(fieldName ?? "").trim().toLowerCase()
  );
  const normalizedApis = apiKeys.map((apiKey) =>
    String(apiKey ?? "").trim().toLowerCase()
  );
  return (Array.isArray(metadataFilters) ? metadataFilters : []).find(
    (definitionRow) => {
      const field = String(definitionRow?.field ?? "").trim().toLowerCase();
      const api = String(definitionRow?.api ?? "").trim().toLowerCase();
      return normalizedFields.includes(field) || normalizedApis.includes(api);
    }
  );
};

const applyPrefillDropdownFilterValue = (
  nextFilter,
  metadataFilters,
  { fieldNames, apiKeys, value }
) => {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) return;
  const definition = resolveFilterDefinitionByFieldOrApi(
    metadataFilters,
    fieldNames,
    apiKeys
  );
  const fieldKey = definition?.field || fieldNames[0];
  if (!fieldKey) return;
  nextFilter[fieldKey] = trimmedValue;
};

const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const getPaginationEnvelope = (response) => {
  const raw = response?.data;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
};

const buildPaginationMeta = (envelope, payload, recordsLength) => ({
  pageNumber: Number(envelope?.pageNumber) || Number(payload?.pageNumber) || 1,
  pageSize: resolveServerPageSizeMeta(
    envelope?.pageSize,
    payload?.pageSize,
    DEFAULT_PAGE_SIZE
  ),
  totalPages: Number(envelope?.totalPages) || 0,
  totalRecords: Number(envelope?.totalRecords) || recordsLength,
});

const mapRowIds = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    id: item?.id ?? item?.Id ?? item?._id,
  }));

/** Empty optional filter fields → null for POST (same idea as all-candidates). */
const normalizeCallExecutiveFilterPayload = (raw = {}) => {
  const out = { ...raw };
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) {
      delete out[key];
      return;
    }
    const v = out[key];
    if (v === "" || v === undefined) out[key] = null;
  });
  return out;
};

const buildPrefillFilterState = (prefill = {}, emptyFilter = {}, metadataFilters = []) => {
  const clientId = String(prefill?.clientId ?? "").trim();
  const clientLabel = String(
    prefill?.clientIdLabel ?? prefill?.clientName ?? ""
  ).trim();

  const nextFilter = { ...emptyFilter };
  const clientDefinition = resolveClientFilterDefinition(metadataFilters);
  const clientFieldKey = clientDefinition?.field || "clientId";

  if (clientId) {
    nextFilter[clientFieldKey] = clientId;
    nextFilter[getAsyncFilterLabelFieldKey(clientFieldKey)] = clientLabel;
  }

  applyPrefillDropdownFilterValue(nextFilter, metadataFilters, {
    fieldNames: ["orderStatus"],
    apiKeys: ["get_order_status", "hire_status_option"],
    value: prefill?.orderStatus,
  });
  applyPrefillDropdownFilterValue(nextFilter, metadataFilters, {
    fieldNames: ["callStatus"],
    apiKeys: ["get_call_status", "get_call_status_two"],
    value: prefill?.callStatus,
  });

  return nextFilter;
};

const Today = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const useVmPreset = searchParams.has("vm");
  const {
    fetchCallExecutiveTodayContextData,
    callExecutiveTodayContextData,
  } = useCallExecutiveContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCommonTableSettingsPageSize("CallExecutiveToday");
  const initialFilterFetchedRef = useRef(false);

  const prefillHandledRef = useRef(false);
  const pendingPrefillRef = useRef(null);

  if (!pendingPrefillRef.current) {
    const fromNavigation = location.state?.prefillFilters;
    const fromStorage = readStoredPrefillFilters();
    pendingPrefillRef.current =
      (fromNavigation && typeof fromNavigation === "object" ? fromNavigation : null) ||
      fromStorage;
  }

  const [toolbarFilterRemountKey, setToolbarFilterRemountKey] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [videoInterviewModalOpen, setVideoInterviewModalOpen] = useState(false);
  const [videoInterviewRow, setVideoInterviewRow] = useState(null);
  /** Toolbar draft filters — API uses applied filter only after Search (see useCallExecutiveAppliedFilter). */
  const [callExecutiveListFilter, setCallExecutiveListFilter] = useState({});
  const {
    initialLoadDoneRef,
    commitDraftAsApplied,
    resolveFilterBase,
    setAppliedFilter,
  } = useCallExecutiveAppliedFilter();
  /** Pipeline segment filter — POST body field `pipelineStage` when set (see CallExecutivePipelineBar). */
  const [pipelineStageKey, setPipelineStageKey] = useState(null);
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const filteredData = useMemo(() => {
    if (!searchQuery?.trim()) return tableData;

    return tableData?.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [tableData, searchQuery]);

  const {
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow,
    activeModalFilterCount,
    modalFilterData: callExecutiveTodayFilterModalData,
    metadataToolbarFilterDefinitions,
    metadataFiltersAll,
    usesMetadataDrivenFilterUi,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(
    callExecutiveTodayContextData?.filters,
    callExecutiveListFilter,
    "CallExecutiveToday"
  );

  const callExecutiveTodayFilterModalDataWithCallStatusTwo = useMemo(
    () =>
      (Array.isArray(callExecutiveTodayFilterModalData)
        ? callExecutiveTodayFilterModalData
        : []
      ).map((filterItem) => {
        if (filterItem?.api === "get_call_status_two") return filterItem;
        if (filterItem?.api !== "get_call_status") return filterItem;
        return {
          ...filterItem,
          api: "get_call_status_two",
        };
      }),
    [callExecutiveTodayFilterModalData]
  );

  useEffect(() => {
    const metadataFilters = callExecutiveTodayContextData?.filters;
    if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
    if (pendingPrefillRef.current?.clientId && !prefillHandledRef.current) return;
    setCallExecutiveListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      metadataFilters.forEach((filterDefinitionRow) => {
        const fieldKey = filterDefinitionRow?.field;
        if (!fieldKey) return;
        if (!(fieldKey in mergedFilterState)) {
          mergedFilterState[fieldKey] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [callExecutiveTodayContextData?.filters]);

  const loadList = useCallback(
    async (overrides = {}, filterOverride) => {
      const filterBase = resolveFilterBase(filterOverride);
      setLoading(true);
      try {
        const payload = normalizeCallExecutiveFilterPayload({
          pageNumber: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          ...filterBase,
          ...(pipelineStageKey ? { pipelineStage: pipelineStageKey } : {}),
          ...(useVmPreset ? { preset: "vm" } : {}),
          ...overrides,
        });
        const response = await submitCallExecutiveTodayFilterData(payload);
        const list = extractList(response);
        const records = Array.isArray(list) ? list : [];
        setTableData(mapRowIds(records));
        const envelope = getPaginationEnvelope(response);
        setPaginationMeta(buildPaginationMeta(envelope, payload, records.length));
      } finally {
        setLoading(false);
      }
    },
    [pipelineStageKey, resolveFilterBase, useVmPreset]
  );

  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;

  useEffect(() => {
    fetchCallExecutiveTodayContextData?.();
  }, [fetchCallExecutiveTodayContextData]);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    if (pendingPrefillRef.current?.clientId) return;
    initialFilterFetchedRef.current = true;
    initialLoadDoneRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setAppliedFilter({});
    loadListRef.current({ pageNumber: 1, pageSize }, {});
  }, [settingsReady, settingsPageSize, setAppliedFilter, initialLoadDoneRef]);

  useEffect(() => {
    if (!settingsReady) return;
    const prefill = pendingPrefillRef.current;
    const clientId = String(prefill?.clientId ?? "").trim();
    if (!clientId || prefillHandledRef.current) return;
    if (!Array.isArray(callExecutiveTodayContextData?.filters) || !callExecutiveTodayContextData.filters.length) {
      return;
    }

    const metadataFilters = callExecutiveTodayContextData.filters;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;

    prefillHandledRef.current = true;
    initialFilterFetchedRef.current = true;
    initialLoadDoneRef.current = true;
    const emptyFilter = buildBaselineEmptyListFilter();
    const mergedFilter = buildPrefillFilterState(prefill, emptyFilter, metadataFilters);

    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setCallExecutiveListFilter(mergedFilter);
    setAppliedFilter(mergedFilter);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);
    loadListRef.current({ pageNumber: 1, pageSize }, mergedFilter);
    pendingPrefillRef.current = null;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [
    location.pathname,
    callExecutiveTodayContextData?.filters,
    buildBaselineEmptyListFilter,
    navigate,
    setAppliedFilter,
    settingsReady,
    settingsPageSize,
    initialLoadDoneRef,
  ]);

  useEffect(() => {
    const columnsFromMetadata = mapApiMetadataTableColumns(
      callExecutiveTodayContextData?.table?.columns
    );
    const mergedCallExecutiveColumns = appendCallExecutiveQuickUpdateColumnIfMissing(
      buildCallExecutiveTableColumnsFromMetadata(columnsFromMetadata)
    );
    setTableMetadata(
      mergedCallExecutiveColumns.length > 0
        ? mergedCallExecutiveColumns
        : columnsFromMetadata
    );
  }, [callExecutiveTodayContextData]);

  const handleFilterReset = useCallback(async () => {
    const emptyFilter = buildBaselineEmptyListFilter();
    setCallExecutiveListFilter(emptyFilter);
    setAppliedFilter(emptyFilter);
    setPipelineStageKey(null);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      emptyFilter
    );
  }, [buildBaselineEmptyListFilter, loadList, paginationMeta.pageSize, setAppliedFilter]);

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter, handleFilterReset]);

  const handleFilterSubmit = async (formData = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      callExecutiveListFilter,
      callExecutiveTodayFilterModalData,
      formData
    );
    setCallExecutiveListFilter(mergedFilter);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      mergedFilter
    );
  };

  const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
    setCallExecutiveListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: nextValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
    setCallExecutiveListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: selectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
    }));
  };

  const handleUpperToolbarFilterSearch = async () => {
    commitDraftAsApplied(callExecutiveListFilter);
    await loadList({
      pageNumber: 1,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleCallExecutiveCall = (row) => {
    const candidateId = String(row?.id ?? "").trim();
    if (!candidateId) {
      toast.error("Candidate id is missing");
      return;
    }
    window.open(`/${urlEndPoint.callDetails}/${candidateId}`, "_blank", "noopener,noreferrer");
  };

  const handleCallExecutiveVideoInterview = (row) => {
    setVideoInterviewRow(row && typeof row === "object" ? { ...row } : {});
    setVideoInterviewModalOpen(true);
  };

  const effectiveColumns =
    tableMetaData?.length > 0 ? tableMetaData : undefined;

  const handleCallExecutiveQuickUpdateBlur = useCallback(
    async (rowRecord, nextValue, fieldAccessor) => {
      // console.log('rowRecord', rowRecord)
      const candidateClientId = rowRecord?.id
        // resolveCallExecutiveQuickUpdateCandidateClientId(rowRecord);
      if (!candidateClientId) {
        toast.error("Candidate client id is missing");
        return;
      }

      const previousValue = resolveCallExecutiveQuickUpdateRowValue(
        rowRecord,
        fieldAccessor
      ).trim();
      const nextTrimmed = String(nextValue ?? "").trim();
      if (previousValue === nextTrimmed) return;

      const response = await submitCallExecutiveQuickUpdate(
        candidateClientId,
        nextTrimmed
      );
      if (response == null || response?.success === false) {
        if (response?.message) toast.error(response.message);
        return;
      }

      const fieldKey = fieldAccessor || "callQuickUpdate";
      setTableData((previousRows) =>
        previousRows.map((row) => {
          if (
            resolveCallExecutiveQuickUpdateCandidateClientId(row) !==
            candidateClientId
          ) {
            return row;
          }
          return { ...row, [fieldKey]: nextTrimmed };
        })
      );

      if (response?.message) {
        toast.success(response.message);
      }
    },
    []
  );

  const handlePipelineTabChange = async (nextKey) => {
    setPipelineStageKey(nextKey);
    setLoading(true);
    try {
      const payload = normalizeCallExecutiveFilterPayload({
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
        ...resolveFilterBase(),
        ...(nextKey ? { pipelineStage: nextKey } : {}),
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
        ...(useVmPreset ? { preset: "vm" } : {}),
      });
      const response = await submitCallExecutiveTodayFilterData(payload);
      const list = extractList(response);
      const records = Array.isArray(list) ? list : [];
      setTableData(mapRowIds(records));
      const envelope = getPaginationEnvelope(response);
      setPaginationMeta(buildPaginationMeta(envelope, payload, records.length));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    await loadList({
      pageNumber: page,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleRowsPerPageChange = async (rows) => {
    await loadList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content call-exec-schedule-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 className="fw-bold mb-0">Today</h5>
              </div>
              <hr />

              {shouldRenderMetadataToolbarFilterRow ? (
                <MetadataFilterToolbar
                  remountKey={toolbarFilterRemountKey}
                  toolbarDefinitions={metadataToolbarFilterDefinitions}
                  listFilter={callExecutiveListFilter}
                  onTextChange={handleToolbarTextFilterInputChange}
                  onSelectChange={handleToolbarReactSelectFilterChange}
                  onSearch={handleUpperToolbarFilterSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={
                    !shouldShowTableHeaderFilterAndResetControls
                  }
                  toolbarDropdownOptionsByFieldName={
                    toolbarDropdownReactSelectOptionsByFieldName
                  }
                  layoutPreset="callExecutive"
                  pageName="CallExecutiveToday"
                />
              ) : null}

              <CallExecutivePipelineBar
                activeKey={pipelineStageKey}
                onTabChange={handlePipelineTabChange}
              />

              {/* <Row className="align-items-center g-2 mb-4 mt-1">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      type="button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row> */}

              {loading ? (
                <Loaders />
              ) : effectiveColumns?.length ? (
                <TableContainer
                  columns={effectiveColumns}
                  data={filteredData}
                  isActionRequired
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta.pageNumber}
                  rowsPerPage={paginationMeta.pageSize}
                  totalPages={paginationMeta.totalPages}
                  totalRecords={paginationMeta.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  setCommonResetFilter={setCommonResetFilter}
                  allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                  activeFilterCount={activeModalFilterCount}
                  pageName="CallExecutiveToday"
                  onCallExecutiveCall={handleCallExecutiveCall}
                  onCallExecutiveVideoInterview={handleCallExecutiveVideoInterview}
                  onCallExecutiveQuickUpdateBlur={handleCallExecutiveQuickUpdateBlur}
                  showCopyCsvPrintToolbar
                />
              ) : (
                <div className="text-muted text-center py-5">Unable to load table columns.</div>
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            pageName="CallExecutiveToday"
            modalTitle="Today"
            handleSubmit={handleFilterSubmit}
            filterData={callExecutiveTodayFilterModalDataWithCallStatusTwo}
            metadataFilterDefinitions={metadataFiltersAll}
            enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
          />

          <VideoInterviewLinkModal
            isOpen={videoInterviewModalOpen}
            onClose={() => {
              setVideoInterviewModalOpen(false);
              setVideoInterviewRow(null);
            }}
            row={videoInterviewRow || {}}
            onSuccess={() =>
              loadList({
                pageNumber: paginationMeta.pageNumber,
                pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
              })
            }
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

Today.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(Today);
